import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  Group,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
  ActionIcon,
} from '@mantine/core';
import { Edit2, Plus } from 'react-feather';
import type { Topic } from '../../../../../types';
import { TopicSelectModal } from '../TopicSelectModal';

/**
 * MED-975 (E1.3d) — Session Details panel.
 *
 * Two fields:
 *   - Session Topic (MED-1091 / E1.3n) — read-only display + pencil that opens
 *     the existing Select Topic modal (pick a known topic or add a custom one).
 *     Persists IMMEDIATELY on confirm at the session-number scope
 *     (session_number_config.topic) via the parent's onTopicSelect — it is NOT
 *     batched into the editor's Save button and is excluded from dirty-state
 *     (AC3, AC4, AC4b, AC8). On load the parent feeds the RESOLVED session-number
 *     topic (not sessions.title — the read/write mismatch this story fixes, AC5).
 *   - Description (AC-SD2) — multi-line plain text. Stored on
 *     session_number_config.description; still saved via the editor's Save.
 *
 * The panel is presentational for description (state + Save live in the parent
 * page). The topic side-effect is delegated to the parent via onTopicSelect so
 * the network call stays in one place; the panel only owns the modal open state.
 */

export interface SessionDetailsPanelProps {
  /**
   * MED-1091 (E1.3n / AC5) — resolved session-number topic. Null → empty state
   * (AC7). Never sessions.title.
   */
  topic: string | null;
  description: string;
  /** Session number, for the modal subtitle and the "applies to all groups" helper copy. */
  sessionNumber: number | null;
  /**
   * MED-1091 (E1.3n / AC10) — whether groups exist for this cohort yet. Drives
   * the helper-copy variant: when false (pre-groups authoring via e1.3m) the
   * copy says "…once groups are created."
   */
  groupsExist: boolean;
  /** Fired when the CM confirms a topic in the modal. The parent persists it immediately. */
  onTopicSelect: (topic: Topic) => void;
  onDescriptionChange: (description: string) => void;
}

export function SessionDetailsPanel({
  topic,
  description,
  sessionNumber,
  groupsExist,
  onTopicSelect,
  onDescriptionChange,
}: SessionDetailsPanelProps) {
  const [topicModalOpened, setTopicModalOpened] = useState(false);

  // AC10 — persistent helper line shown in BOTH the empty and filled states.
  // Mirrors the View-by-Session edit copy; uses the no-groups variant when
  // groups don't exist yet (pre-groups authoring via e1.3m).
  const sessionLabel = sessionNumber !== null ? `Session ${sessionNumber}` : 'this session';
  const helperText = groupsExist
    ? `This topic applies to all group sessions for ${sessionLabel}. Changing it here or from a session's topic picker keeps both in sync.`
    : `This topic applies to all group sessions for ${sessionLabel} once groups are created.`;

  const handleSelect = (selected: Topic) => {
    onTopicSelect(selected);
    setTopicModalOpened(false);
  };

  return (
    <Card withBorder padding="lg" radius="md">
      <Group justify="space-between" align="center" mb="md">
        <Title order={3}>Session Details</Title>
      </Group>
      <Stack gap="md">
        {/* MED-1091 (E1.3n / AC2, AC3, AC7) — Session Topic: read-only display +
            pencil, or an "Add session topic" affordance when empty. */}
        <Box>
          <Text size="sm" fw={500} mb={4}>
            Session Topic
          </Text>
          {topic && topic.trim().length > 0 ? (
            <TextInput
              readOnly
              value={topic}
              onClick={() => setTopicModalOpened(true)}
              styles={{ input: { cursor: 'pointer' } }}
              rightSection={
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  onClick={() => setTopicModalOpened(true)}
                  aria-label="Edit session topic"
                  data-testid="session-topic-edit-button"
                >
                  <Edit2 size={16} />
                </ActionIcon>
              }
              data-testid="session-topic-display"
            />
          ) : (
            <Button
              variant="default"
              leftSection={<Plus size={16} />}
              onClick={() => setTopicModalOpened(true)}
              data-testid="session-topic-empty-button"
            >
              Add session topic
            </Button>
          )}
          {/* AC10 — helper line lives on the page/field (NOT inside the modal). */}
          <Text size="xs" c="dimmed" mt={6} data-testid="session-topic-helper">
            {helperText}
          </Text>
        </Box>

        {/* MED-1102 (Facilitation Foundation) — Session Description: helper line
            makes the audience explicit (it's shown to leaders + participants on
            the pre-session pages, MED-1101), nudging authors toward
            audience-appropriate copy. Microcopy only — no behavior/data change.
            Inherits the live_session_delivery gating from SessionContentEditorPage,
            which fails closed when the flag is off. Mirrors the Session Topic
            helper pattern above. */}
        <Box>
          <Textarea
            label="Description"
            placeholder="What is this session about?"
            minRows={3}
            autosize
            value={description}
            onChange={e => onDescriptionChange(e.currentTarget.value)}
            data-testid="agenda-session-description"
          />
          {/* AC2/AC3 — persistent helper line beneath the field, visible in both
              the empty and filled states. Copy is fixed per the ticket. */}
          <Text size="xs" c="dimmed" mt={6} data-testid="session-description-helper">
            Shown to session leaders and participants to help them prepare for the session.
          </Text>
        </Box>
      </Stack>

      {/* AC3 — the existing Select Topic modal, reused unchanged. Session-number
          scoped: the participant-facing display_name input is opted out
          (showDisplayNameField=false) because the editor writes
          session_number_config.topic, which has no row to attach an override to. */}
      <TopicSelectModal
        opened={topicModalOpened}
        onClose={() => setTopicModalOpened(false)}
        onSelect={handleSelect}
        sessionNumber={sessionNumber ?? undefined}
        showDisplayNameField={false}
      />
    </Card>
  );
}
